import { inject, Injectable, signal } from '@angular/core';

import { GatewayId } from '../site-gateway/models/gateway';

import { ToolboxGateway } from './toolbox.gateway';

/**
 * The installer's toolboxes (test installations), shared by every screen that flags them.
 * The state lives as long as the page: logging out leaves the application, so there is no session reset.
 */
@Injectable({ providedIn: 'root' })
export class ToolboxStore {
    private readonly gateway = inject(ToolboxGateway);
    private readonly state = signal<readonly GatewayId[]>([]);
    private request: Promise<void> | undefined;
    private generation = 0;
    private appliedGeneration = 0;

    /** Loads the list on the first call; later calls share that request, or retry it after a failure. */
    load(): Promise<void> {
        this.request ??= this.fetch();
        return this.request;
    }

    reload(): Promise<void> {
        this.request = this.fetch();
        return this.request;
    }

    isToolbox(gatewayIds: readonly GatewayId[] = []): boolean {
        return gatewayIds.some((gatewayId) => this.state().includes(gatewayId));
    }

    private async fetch(): Promise<void> {
        const generation = ++this.generation;
        try {
            const gatewayIds = await this.gateway.getToolboxes();
            // An answer older than the one already applied is stale.
            if (generation > this.appliedGeneration) {
                this.appliedGeneration = generation;
                this.state.set(gatewayIds);
            }
        } catch {
            // Keep the last list received (none before the first success); the next load() asks again.
            if (generation === this.generation) this.request = undefined;
        }
    }
}
